import { NO_COMBO_STEP, TICK_MS, comboStep, stripOfStep, type Ability, type Rules } from "odyssey-shared";

/**
 * Which strips the page's own swing plays (ADR-073's amendment "the Strike combo and the Heavy Strike chain", items 2,
 * 4 and 8): the step of a combo the page predicts with the server's own rule (`comboStep`) over the ready tick it holds
 * for the ability and the tick it expects the act at, corrected by the step `used` carries; and a chain's later strips
 * and their times from the press, with the inputs its root holds. Each ability's chain plan is built once from the
 * rules, so a press allocates nothing.
 */

/**
 * A chain's later strips and when each starts after the press, and the inputs its root holds.
 */
export interface ChainPlan {
  readonly strips: readonly string[];
  readonly offsetsMs: readonly number[];
  readonly rootInputs: number;
}

/**
 * What a confirmed step changes: the strip the page started and the one the server chose.
 */
export interface StripSwap {
  from: string;
  to: string;
}

/**
 * The plan.
 */
export class SwingPlan {
  private readonly chains: (ChainPlan | null)[];
  private comboAbility = 0;
  private comboStepNow = NO_COMBO_STEP;
  private beforeAbility = 0;
  private beforeStep = NO_COMBO_STEP;
  private lastPress = 0;
  private readonly swapped: StripSwap = { from: "", to: "" };

  /**
   * @param rules The page's rules: each ability's combo and chain.
   */
  public constructor(private readonly rules: Rules) {
    this.chains = [null, ...rules.tables.abilities.map((ability) => (ability.chain === null ? null : { strips: ability.chain.hits.slice(1).map((hit) => hit.strip), offsetsMs: ability.chain.hits.slice(1).map((hit) => hit.atTicks * TICK_MS), rootInputs: ability.chain.rootTicks }))];
  }

  /**
   * The chain plan of an ability.
   *
   * @param number The ability's number.
   * @returns Its plan, or null for an ability with no chain.
   */
  public chainOf(number: number): ChainPlan | null {
    return this.chains[number] ?? null;
  }

  /**
   * The strip a press of an ability starts, and what the page now holds as its last use: the combo's predicted step
   * for a combo ability, its own strip otherwise, which also forgets the combo.
   *
   * @param number The ability's number.
   * @param ability The ability.
   * @param previousReady The ready tick the page holds for the ability before this press.
   * @param tick The tick the page expects the act at.
   * @returns The strip.
   */
  public press(number: number, ability: Ability, previousReady: number, tick: number): string {
    const combo = ability.combo;
    this.lastPress = number;
    this.beforeAbility = this.comboAbility;
    this.beforeStep = this.comboStepNow;
    if (combo === null) {
      this.comboAbility = 0;
      this.comboStepNow = NO_COMBO_STEP;
      return ability.strip;
    }
    const previous = this.comboAbility === number ? this.comboStepNow : NO_COMBO_STEP;
    this.comboAbility = number;
    this.comboStepNow = comboStep(previous, previousReady, tick, combo.graceTicks, combo.strips.length);
    return stripOfStep(ability, this.comboStepNow);
  }

  /**
   * Reads the step a `used` of the page's newest act carries: when it differs from the step the page predicted, the
   * page holds the server's from now and is told which strip to swap for which.
   *
   * @param number The ability's number.
   * @param step The step `used` carries.
   * @returns The swap, or null when the page predicted the server's step or the ability is not the combo it holds.
   */
  public confirm(number: number, step: number): Readonly<StripSwap> | null {
    if (number !== this.comboAbility || step === this.comboStepNow) {
      return null;
    }
    const ability = this.rules.tables.abilities[number - 1] as Ability;
    this.swapped.from = stripOfStep(ability, this.comboStepNow);
    this.swapped.to = stripOfStep(ability, step);
    this.comboStepNow = step;
    return this.swapped;
  }

  /**
   * Reads a `refused` of the page's newest act of an ability: when it is the last press, of a combo ability or not, the
   * server changed nothing, so the combo goes back to what the page held before that press.
   *
   * @param number The refused ability's number.
   */
  public refused(number: number): void {
    if (number === this.lastPress) {
      this.comboAbility = this.beforeAbility;
      this.comboStepNow = this.beforeStep;
    }
  }

  /**
   * Forgets the combo, as a session ends.
   */
  public reset(): void {
    this.lastPress = 0;
    this.comboAbility = 0;
    this.comboStepNow = NO_COMBO_STEP;
  }
}
